import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../api';
import { useToast } from '../components/Toast';

const ContactPage = ({ navigate }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      if (!response.ok) {
        throw new Error(data?.error || data?.detail || 'Unable to send your message. Please try again.');
      }
      setSubmitted(true);
      toast.success('Message sent. We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 6000);
    } catch (error) {
      const msg = error.message === 'Failed to fetch'
        ? 'Unable to connect to the server. Please try again or WhatsApp us.'
        : (error.message || 'There was an error submitting your message.');
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-cream">
      <Navbar navigate={navigate} />
      
      {/* Hero Section */}
      <section className="relative gradient-bg text-white pt-28 pb-12 sm:pt-32 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6">Contact Us</h1>
          <p className="text-base sm:text-xl md:text-2xl text-sand max-w-3xl mx-auto">
            We're here to help you plan your spiritual journey
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-12 sm:py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">📞</span>
                </div>
                <h3 className="text-xl font-bold text-ink mb-2">Phone</h3>
                <p className="text-ink/65 mb-2">+65 6294 8044</p>
                <p className="text-sm text-ink/50">Mon-Fri: 9AM - 6PM</p>
              </div>

              <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">📧</span>
                </div>
                <h3 className="text-xl font-bold text-ink mb-2">Email</h3>
                <p className="text-ink/65 mb-2">enquiry@tmfouzy.sg</p>
                <p className="text-sm text-ink/50">We reply within 24 hours</p>
              </div>

              <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">💬</span>
                </div>
                <h3 className="text-xl font-bold text-ink mb-2">WhatsApp</h3>
                <p className="text-ink/65 mb-2">+65 9820 1134</p>
                <p className="text-sm text-ink/50">Available 24/7</p>
              </div>

              <div className="bg-white p-5 sm:p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-cream rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">📍</span>
                </div>
                <h3 className="text-xl font-bold text-ink mb-2">Office</h3>
                <p className="text-ink/65 mb-2">390 Victoria St #03-15</p>
                <p className="text-ink/65">Singapore 188061</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-5 sm:p-8 md:p-12 rounded-2xl shadow-lg">
                <h2 className="text-2xl sm:text-3xl font-bold text-ink mb-6">Send Us a Message</h2>
                
                {submitted && (
                  <div className="mb-6 bg-cream border border-accent text-primary px-4 py-3 rounded-lg">
                    Thank you! Your message has been sent successfully. We'll get back to you soon.
                  </div>
                )}
                {formError && (
                  <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {formError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-ink/80 font-semibold mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-sand rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-ink/80 font-semibold mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-sand rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-ink/80 font-semibold mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-sand rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                        placeholder="+65 1234 5678"
                      />
                    </div>
                    <div>
                      <label className="block text-ink/80 font-semibold mb-2">Subject *</label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-sand rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent">
                        <option value="">Select a subject</option>
                        <option value="umrah">Umrah Package Inquiry</option>
                        <option value="hajj">Hajj Package Inquiry</option>
                        <option value="booking">Booking Assistance</option>
                        <option value="general">General Question</option>
                        <option value="feedback">Feedback</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-ink/80 font-semibold mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-3 border border-sand rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                      placeholder="Tell us how we can help you..."></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-primary hover:bg-secondary text-white px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    {submitting ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4">Visit Our Office</h2>
            <p className="text-base sm:text-xl text-ink/65">We'd love to meet you in person</p>
          </div>
          
          <div className="bg-sand rounded-2xl overflow-hidden shadow-lg h-56 sm:h-80 md:h-96">
            <iframe
              title="TM Fouzy office location"
              src="https://www.google.com/maps?q=390+Victoria+Street+Singapore+188061&output=embed"
              className="w-full h-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-ink/65">Quick answers to common questions</p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'What documents do I need for Umrah?',
                a: 'You will need a valid passport (minimum 6 months validity), passport-sized photos, and vaccination certificates. We will guide you through the entire documentation process.'
              },
              {
                q: 'How far in advance should I book?',
                a: 'We recommend booking at least 2-3 months in advance to secure the best rates and availability, especially during peak seasons.'
              },
              {
                q: 'What is included in the package price?',
                a: 'Our packages typically include flights, accommodation, transportation, visa processing, and guided tours. Specific inclusions vary by package.'
              },
              {
                q: 'Do you offer group discounts?',
                a: 'Yes! We offer special rates for groups of 10 or more. Contact us for a customized quote.'
              },
              {
                q: 'What is your cancellation policy?',
                a: 'Cancellation policies vary by package and timing. Please refer to our terms and conditions or contact us for specific details.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                <h3 className="text-lg font-bold text-ink mb-2">{faq.q}</h3>
                <p className="text-ink/65">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 gradient-bg text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl mb-8 text-sand">
            Browse our packages and book your spiritual journey today
          </p>
          <button 
            onClick={() => navigate('category', { category: 'umrah-packages' })}
            className="gradient-gold text-ink hover:opacity-90 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105">
            View Packages
          </button>
        </div>
      </section>

      <Footer navigate={navigate} />
    </div>
  );
};

export default ContactPage;
